"use strict"

import React, { useEffect, useMemo, useState } from "react"
import deepEqual from "deep-equal"
import { Modal, Button, FormControl } from "react-bootstrap"

import { Required } from "../../../shared_components/widgets/utils"
import ReactSelectionElement from "../../../shared_components/widgets/ReactSelectionElement"
import { updateObject } from "../../../Loaders"
import { withFermManagementConfig } from "../../../config/FermManagementConfigProvider"
import ToggleSwitch from "./StartRunModal/ToggleSwitch"
import { logToServer } from "../../../ErrorBoundary"
import { equalArrays } from "../../../shared_components/utils"
import { cleanSubstring } from "./StartRunModal/utils"
import ChangeLogTable, { ChangeLogButton } from "./ChangeLogTable"

function RunMetadataFieldsModal({
  show,
  onHide,
  title = "",
  siteRunMetadata = {},
  menuTypeOptions = [],
  getRunMetaFields,
  intl,
}) {
  const [rows, setRows] = useState([])
  const [original, setOriginal] = useState([])
  const [warning, setWarning] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const [showEditedOnly, setShowEditedOnly] = useState(false)
  const [didChange, setDidChange] = useState(false)
  const [expandedLogsByFieldId, setExpandedLogsByFieldId] = useState({})

  const metadataRows = useMemo(() => {
    return Object.values(siteRunMetadata || {}).sort((a, b) => (a.id || 0) - (b.id || 0))
  }, [siteRunMetadata])

  useEffect(() => {
    setRows(metadataRows)
    setOriginal(metadataRows)
    setWarning("")
    setExpandedLogsByFieldId({})
  }, [show, metadataRows])

  const updateFieldValue = (id, fieldName, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) {
          return row
        }
        return { ...row, [fieldName]: value }
      }),
    )
  }

  useEffect(() => {
    const hadChange = rows.filter(
      (r) =>
        !deepEqual(
          r,
          original.find((o) => o.id === r.id),
        ),
    )
    setDidChange(hadChange.length > 0)
  }, [rows])

  const saveMetadataFields = async (e) => {
    e.preventDefault()
    setWarning("")
    setIsSaving(true)
    setShowEditedOnly(true)

    let missingRequired = false
    const editedPayload = rows
      .filter(
        // Only bother passing through field entries that have changed
        (r) =>
          !deepEqual(
            r,
            original.find((o) => o.id === r.id),
          ),
      )
      .map((r) => {
        const { change_logs, newField, ...info } = r // Get rid of fields not expected to be received by endpoint
        if (!missingRequired && (!info?.field || !info?.label)) {
          missingRequired = true
        }
        if (newField) {
          // Remove dummy id before sending to backend
          const { id, ...final } = info
          return final
        } else {
          return info
        }
      })

    if (missingRequired) {
      alert(
        intl.formatMessage({
          id: "general.required.form.fields.empty",
          defaultMessage: "Please fill out all required form fields",
        }),
      )
      setIsSaving(false)
      return
    }

    updateObject(`/warehouse/run-metafields/update_fields/`, editedPayload, true)
      .then((_) => {
        getRunMetaFields && getRunMetaFields()
        onHide()
      })
      .catch((error) => {
        let errorMessage = "\n"
        console.error(error)
        logToServer(error)
        error.json().then((data) => {
          Object.entries(data).forEach(([key, value], idx) => {
            errorMessage = `${errorMessage}${idx > 0 ? "; " : ""}${key}: ${value}`
          })
          console.error(errorMessage)
          logToServer(errorMessage)
          setWarning(errorMessage)
        })
        setIsSaving(false)
      })
  }

  const [newID, setNewID] = useState(-1)
  const doAddField = () => {
    setRows([
      {
        id: newID,
        newField: true,
        allow_custom_input: false,
        archived_field: false,
        default_value: "",
        field: "",
        field_info: "",
        label: "",
        menu_type: "text",
        options: [],
        required: false,
      },
      ...rows,
    ])
    setNewID(newID - 1)
  }

  const cleanTextString = (e) => e.target.value.replace(/[^a-zA-Z0-9 _-]/g, "")

  return (
    <Modal
      size={"xl"}
      dialogClassName={"common-modal"}
      show={show}
      onHide={onHide}
      style={{ overflowX: "scroll", overflowY: "scroll" }}
      backdrop={"static"}
    >
      <Modal.Header closeButton>
        <Modal.Title className={"text-center"}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {warning && <p className={"error-message"}>{warning}</p>}
        <div style={{ display: "flex", alignItems: "center" }}>
          <ToggleSwitch
            intl={intl}
            idVal={"showArchived"}
            value={showArchived}
            show={original.filter((o) => o.archived_field).length > 0}
            labelInfo={{
              id: "fermmanagement.run.metadata.show.archived",
              defaultMessage: "Show Archived Fields",
            }}
            onToggle={(e) => setShowArchived(e.target.checked)}
            toggleStyle={{}}
          />
          &nbsp;&nbsp;
          <ToggleSwitch
            intl={intl}
            idVal={"showEditedOnly"}
            value={showEditedOnly}
            show={didChange}
            labelInfo={{
              id: "fermmanagement.run.metadata.show.edited.only",
              defaultMessage: "Show Edited Fields Only",
            }}
            onToggle={(e) => setShowEditedOnly(e.target.checked)}
            toggleStyle={{}}
          />
          <div style={{ marginLeft: "auto" }}>
            <Button variant={"outline-primary"} size={"sm"} onClick={doAddField} disabled={isSaving}>
              <span className={"fa fa-plus-circle"} />
              &nbsp;
              {intl.formatMessage({ id: "general.add", defaultMessage: "Add" })}
            </Button>
          </div>
        </div>
        {rows
          .filter((r) => {
            const og = original.find((o) => o.id === r.id) || {}
            if (showEditedOnly) {
              return !deepEqual(r, og)
            } else {
              return !og?.archived_field || showArchived
            }
          })
          .map((r) => {
            const og = original.find((o) => o.id === r.id) || {}
            const fieldChange = !deepEqual(r, og)
            const changeLogs = r.change_logs || []
            return (
              <div key={`field${r.id}`} id={`field${r.id}`} style={{ fontSize: "90%" }}>
                <hr />
                <div
                  style={{
                    padding: "15px",
                    borderRadius: "5px",
                    backgroundColor: r.newField
                      ? "var(--lanzalake-green-lighter)"
                      : r.archived_field
                        ? "var(--lanzalake-red-light)"
                        : fieldChange
                          ? "var(--lanzalake-blue-bright)"
                          : "",
                  }}
                >
                  <div className={"row"} style={r.newField ? {} : { alignItems: "self-end" }}>
                    <div className={"col-4"}>
                      <div>
                        {og.archived_field && (
                          <>
                            <span className={"fa fa-trash"} />
                            &nbsp;
                          </>
                        )}
                        <EditSpan show={og.field !== r.field} />
                        <label>
                          <b>
                            {intl.formatMessage({ id: "general.field", defaultMessage: "Field" })}
                            {r.newField && <Required />}:
                          </b>
                        </label>
                      </div>
                      <input
                        type={"text"}
                        className={"form-control"}
                        value={r.field || ""}
                        disabled={!r.newField}
                        style={r.field ? {} : { borderColor: "var(--lanzalake-red)" }}
                        onChange={(e) => updateFieldValue(r.id, "field", cleanTextString(e))}
                      />
                    </div>
                    <div className={"col-8"} style={{ display: "flex" }}>
                      {r.newField ? (
                        <div style={{ marginLeft: "auto" }}>
                          <Button
                            variant={"outline-primary"}
                            size={"sm"}
                            onClick={() => setRows(rows.filter((row) => row.id !== r.id))}
                          >
                            <span className={"fa fa-trash"} />
                            &nbsp;
                            {intl.formatMessage({
                              id: "general.delete",
                              defaultMessage: "Delete",
                            })}
                          </Button>
                        </div>
                      ) : (
                        changeLogs.length > 0 && (
                          <ChangeLogButton
                            intl={intl}
                            onClick={() =>
                              setExpandedLogsByFieldId((prev) => ({
                                ...prev,
                                [r.id]: !prev[r.id],
                              }))
                            }
                            isExpanded={expandedLogsByFieldId[r.id]}
                          />
                        )
                      )}
                    </div>
                  </div>
                  {expandedLogsByFieldId[r.id] && <ChangeLogTable intl={intl} changeLogs={changeLogs} />}
                  <div className={"row one-rem-margin-bottom"} style={{ marginTop: "1rem" }}>
                    <div className={"col-4"}>
                      <div>
                        <EditSpan show={og.label !== r.label} />
                        <label>
                          <b>
                            {intl.formatMessage({ id: "general.label", defaultMessage: "Label" })}
                            <Required />
                          </b>
                          :
                        </label>
                        <FormControl
                          value={r.label || ""}
                          style={r.label ? {} : { borderColor: "var(--lanzalake-red)" }}
                          onChange={(e) => updateFieldValue(r.id, "label", e.target.value)}
                        />
                      </div>
                      <div style={{ display: "inline-flex", marginTop: "1rem" }}>
                        {!r.newField && (
                          <>
                            <div style={{ display: "flex", alignItems: "center" }}>
                              <EditSpan show={og.archived_field !== r.archived_field} />
                              <ToggleSwitch
                                intl={intl}
                                idVal={`fieldArchived${r.id}`}
                                value={r.archived_field}
                                show={true}
                                labelInfo={{
                                  id: "general.archive",
                                  defaultMessage: "Archive",
                                }}
                                popoverInfo={{
                                  id: "fermmanagement.run.metadata.field.archived.info",
                                  defaultMessage:
                                    "Will stop presenting this field option in form for new runs",
                                }}
                                onToggle={(e) => updateFieldValue(r.id, "archived_field", e.target.checked)}
                              />
                            </div>
                            &nbsp;&nbsp;
                          </>
                        )}
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <EditSpan show={og.required !== r.required} />
                          <ToggleSwitch
                            intl={intl}
                            idVal={`fieldRequired${r.id}`}
                            value={r.required}
                            show={true}
                            labelInfo={{
                              id: "general.required",
                              defaultMessage: "Required",
                            }}
                            popoverInfo={{
                              id: "fermmanagement.run.metadata.field.required.info",
                              defaultMessage:
                                "Will force this form field to be filled out prior to allowing the starting of a run",
                            }}
                            onToggle={(e) => updateFieldValue(r.id, "required", e.target.checked)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className={"col-8"}>
                      <div>
                        <EditSpan show={og.field_info !== r.field_info} />
                        <label>
                          <b>{intl.formatMessage({ id: "general.details", defaultMessage: "Details" })}</b>:
                        </label>
                      </div>
                      <textarea
                        className={"form-control"}
                        rows={4}
                        value={r.field_info || ""}
                        onChange={(e) => updateFieldValue(r.id, "field_info", e.target.value)}
                      />
                    </div>
                  </div>
                  <div className={"row"}>
                    <div className={"col-4"}>
                      <ReactSelectionElement
                        name={"menuType"}
                        list={menuTypeOptions.map((menuType) => ({
                          value: menuType,
                        }))}
                        label={
                          <span>
                            <EditSpan show={og.menu_type !== r.menu_type} />
                            {intl.formatMessage({
                              id: "general.type",
                              defaultMessage: "Type",
                            })}
                          </span>
                        }
                        boldLabel={true}
                        value={{ value: r.menu_type || "text" }}
                        onValueChange={(_, value) => {
                          if (value?.value !== r.menu_type) {
                            updateFieldValue(r.id, "menu_type", value?.value || "text")
                            // Change default_value as well when type changes as the variable type can be different
                            // between different menu types (array vs. string etc.) rather than dealing with
                            // some other way to handle (e.g. multi select array needing to go to string (if using comma
                            // join then questions about allowing commas to be part of the string/options themselves))
                            updateFieldValue(
                              r.id,
                              "default_value",
                              value?.value === "multi-dropdown" ? [] : null,
                            )
                          }
                        }}
                        isMulti={false}
                        isClearable={false}
                      />
                    </div>
                    <div className={"col-8"}>
                      {["dropdown", "multi-dropdown"].includes(r.menu_type) && (
                        <>
                          <div className={"one-rem-margin-bottom"}>
                            <div style={{ display: "flex" }}>
                              <EditSpan show={!equalArrays(og.options, r.options)} />
                              <label>
                                <b>
                                  {intl.formatMessage({ id: "general.options", defaultMessage: "Options" })}
                                  <Required />
                                </b>
                                :
                              </label>
                              <div style={{ display: "flex", marginLeft: "auto", alignItems: "center" }}>
                                <EditSpan show={og.allow_custom_input !== r.allow_custom_input} />
                                <ToggleSwitch
                                  intl={intl}
                                  idVal={"allowCustomInput"}
                                  value={r.allow_custom_input}
                                  show={true}
                                  labelInfo={{
                                    id: "fermmanagement.run.metadata.allow.custom.input",
                                    defaultMessage: "Allow Custom Input",
                                  }}
                                  popoverInfo={{
                                    id: "fermmanagement.run.metadata.allow.custom.input.info",
                                    defaultMessage:
                                      "Will allow 'custom' text/entry and not limit value to a default (controlled) selection found in the options list",
                                  }}
                                  onToggle={(e) =>
                                    updateFieldValue(r.id, "allow_custom_input", e.target.checked)
                                  }
                                />
                              </div>
                            </div>
                            <FormControl
                              as={"textarea"}
                              rows={5}
                              value={(r.options || []).join("\n")}
                              onChange={(e) =>
                                updateFieldValue(
                                  r.id,
                                  "options",
                                  e.target.value.split("\n").map((o) => cleanSubstring(o)),
                                )
                              }
                            />
                            <small>
                              {intl.formatMessage({
                                id: "general.one.option.per.line",
                                defaultMessage: "One option per line",
                              })}
                            </small>
                          </div>
                        </>
                      )}
                      <div>
                        <div>
                          <EditSpan show={og.default_value !== r.default_value} />
                          <label>
                            <b>
                              {intl.formatMessage({
                                id: "general.default.value",
                                defaultMessage: "Default Value",
                              })}
                            </b>
                            :
                          </label>
                        </div>
                        {r.menu_type === "textarea" ? (
                          <textarea
                            className={"form-control"}
                            rows={4}
                            value={r.default_value || ""}
                            onChange={(e) => updateFieldValue(r.id, "default_value", e.target.value)}
                          />
                        ) : ["dropdown", "multi-dropdown"].includes(r.menu_type) ? (
                          <ReactSelectionElement
                            name={"default_value"}
                            list={r.options.map((o) => ({
                              value: o,
                            }))}
                            value={
                              !r.default_value ||
                              (Array.isArray(r.default_value) && r.default_value.length === 0)
                                ? null
                                : Array.isArray(r.default_value)
                                  ? r.default_value.map((v) => {
                                      return { value: v || "" }
                                    })
                                  : { value: r.default_value || "" }
                            }
                            onValueChange={(_, selection) =>
                              updateFieldValue(
                                r.id,
                                "default_value",
                                Array.isArray(selection) ? selection.map((v) => v?.value) : selection?.value,
                              )
                            }
                            isMulti={r.menu_type === "multi-dropdown"}
                          />
                        ) : (
                          <input
                            type={r.menu_type === "number" ? "number" : "text"}
                            className={"form-control"}
                            value={r.default_value || ""}
                            onChange={(e) => updateFieldValue(r.id, "default_value", e.target.value)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant={"outline-primary"}
          style={{ marginRight: "auto" }}
          onClick={onHide}
          disabled={isSaving}
        >
          {intl.formatMessage({ id: "general.closeButton", defaultMessage: "Close" })}
        </Button>
        <div style={{ marginLeft: "auto" }}>
          <Button
            variant={"outline-primary"}
            onClick={() => {
              setRows(original)
              setWarning("")
              setShowEditedOnly(false)
              setShowArchived(false)
            }}
            disabled={isSaving || !didChange}
          >
            {intl.formatMessage({ id: "general.reset", defaultMessage: "Reset" })}
          </Button>
          &nbsp;&nbsp;
          <Button variant={"primary"} onClick={saveMetadataFields} disabled={isSaving || !didChange}>
            {isSaving
              ? `${intl.formatMessage({ id: "general.loading", defaultMessage: "Loading" })}...`
              : intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export const EditSpan = ({ show }) => {
  return (
    <>
      {show && (
        <>
          <span className="fa fa-pencil-square-o" />
          &nbsp;
        </>
      )}
    </>
  )
}

export default withFermManagementConfig(RunMetadataFieldsModal)
